import type { ErrorCode, ErrorMessage, SessionPublic, StreamFormat } from '@auxcast/protocol';
import { LIMITS, isRetryableClose, parseFormat, parseServerMessage } from '@auxcast/protocol';

import type { BackoffOptions } from './backoff';
import { DEFAULT_BACKOFF, backoffDelay } from './backoff';

/**
 * `parseServerMessage` only checks the `t` discriminator and then casts — it does
 * not validate payload shape (that is deliberate in the frozen protocol package, so
 * an unknown field never costs a live stream). Everything below therefore re-checks
 * every field this client actually reads before touching it: a relay bug, a stale
 * deployment or a reverse proxy injecting JSON must not be able to throw out of the
 * WebSocket `onmessage` handler, which would strand the client in `connecting`
 * forever with no close event to recover from.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asSessionPublic(value: unknown): SessionPublic | null {
  if (!isRecord(value)) return null;
  const startedAt = value['startedAt'];
  return {
    name: typeof value['name'] === 'string' && value['name'] !== '' ? value['name'] : 'Untitled session',
    live: value['live'] === true,
    startedAt: typeof startedAt === 'number' && Number.isFinite(startedAt) ? startedAt : null,
  };
}

/**
 * A format we cannot parse is worse than no format: it would reach the pipeline as
 * `NaN` channel counts and sample rates and silently kill playback, so it is
 * normalised to null and the stream is reported as unplayable instead.
 */
function asFormat(value: unknown): StreamFormat | null {
  const result = parseFormat(value);
  return result.ok ? result.value : null;
}

function asCount(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return Math.trunc(value);
}

export type ConnectionState =
  /** Nothing running: before the first connect, or after an explicit stop. */
  | 'idle'
  /** Socket open in progress, or open but `ready` not yet received. */
  | 'connecting'
  /** Attached to the session, but no sender is streaming. The common case. */
  | 'waiting'
  /** Frames are arriving. */
  | 'live'
  /** Dropped; a retry is scheduled. */
  | 'reconnecting'
  /** Terminal — needs the user to do something (wrong passcode, dead session). */
  | 'failed';

export interface ListenerClientHandlers {
  onState?(state: ConnectionState, detail: StateDetail): void;
  /** Raw binary frame, still including the 16-byte header. */
  onFrame?(data: ArrayBuffer): void;
  onSession?(session: SessionPublic, sessionId: string): void;
  /** The sender renamed a live session (`meta`). */
  onName?(name: string): void;
  onFormat?(format: StreamFormat | null): void;
  onListeners?(count: number): void;
  onStreamEnd?(reason: string): void;
  onProtocolError?(error: ErrorMessage): void;
  onRoundTrip?(rttMs: number): void;
}

export interface StateDetail {
  /** Retry number, for the "reconnecting" state. */
  attempt?: number;
  /** Milliseconds until the next attempt. */
  retryInMs?: number;
  /** Why we ended up here, safe to show the user. */
  reason?: string;
}

/** The subset of `WebSocket` the client uses, so tests can supply a fake. */
export interface SocketLike {
  binaryType: string;
  readyState: number;
  send(data: string | ArrayBufferLike): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export interface ListenerClientOptions {
  url: string;
  passcode?: string | null;
  handlers?: ListenerClientHandlers;
  backoff?: BackoffOptions;
  createSocket?: (url: string) => SocketLike;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  now?: () => number;
  random?: () => number;
}

const OPEN = 1;

/**
 * Listener side of `WS /listen/:id` — docs/PROTOCOL.md §4.2, §4.4.
 *
 * Owns the socket lifecycle only: it hands raw binary frames to a callback and never
 * touches the audio pipeline, which keeps the reconnect logic testable in Node.
 */
export class ListenerClient {
  private readonly options: Required<
    Pick<ListenerClientOptions, 'createSocket' | 'setTimer' | 'clearTimer' | 'now' | 'random'>
  > &
    ListenerClientOptions;

  private socket: SocketLike | null = null;
  private state: ConnectionState = 'idle';
  private stopped = true;
  private attempt = 0;
  private fatal: ErrorMessage | null = null;
  private retryHandle: unknown = null;
  private heartbeatHandle: unknown = null;
  private lastMessageAt = 0;
  private passcode: string | null;
  private sawReady = false;

  constructor(options: ListenerClientOptions) {
    this.options = {
      createSocket: (url: string) => new WebSocket(url) as unknown as SocketLike,
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
      now: () => Date.now(),
      random: () => Math.random(),
      ...options,
    };
    this.passcode = options.passcode ?? null;
  }

  getState(): ConnectionState {
    return this.state;
  }

  /** The fatal `error` message that ended the connection, if any. */
  getFatalError(): ErrorMessage | null {
    return this.fatal;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.fatal = null;
    this.connect();
  }

  /** Retry after a `failed` state, e.g. once the user has typed a new passcode. */
  retryWith(passcode: string | null): void {
    this.passcode = passcode;
    this.stop();
    this.start();
  }

  stop(): void {
    this.stopped = true;
    this.clearRetry();
    this.stopHeartbeat();
    this.teardownSocket(1000, 'listener stopped');
    this.setState('idle', {});
  }

  private connect(): void {
    this.clearRetry();
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting', { attempt: this.attempt });
    this.sawReady = false;

    let socket: SocketLike;
    try {
      socket = this.options.createSocket(this.options.url);
    } catch {
      this.scheduleRetry('could not open a connection');
      return;
    }

    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.lastMessageAt = this.options.now();

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.setState('connecting', {});
      this.send({
        t: 'hello',
        v: LIMITS.PROTOCOL_VERSION,
        role: 'listener',
        passcode: this.passcode,
      });
      this.startHeartbeat();
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      this.lastMessageAt = this.options.now();
      this.handleMessage(event.data);
    };

    socket.onerror = () => {
      /* `close` always follows; retry scheduling lives there so it happens once. */
    };

    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.stopHeartbeat();
      this.handleClose(event.code, event.reason);
    };
  }

  private handleMessage(data: unknown): void {
    if (typeof data === 'string') {
      this.handleControl(data);
      return;
    }
    if (data instanceof ArrayBuffer) {
      this.options.handlers?.onFrame?.(data);
      return;
    }
    if (ArrayBuffer.isView(data)) {
      const view = data as ArrayBufferView;
      this.options.handlers?.onFrame?.(
        view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer,
      );
      return;
    }
    // A Blob arrives only if binaryType was ignored; read it rather than drop audio.
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      void data.arrayBuffer().then(
        (buffer) => this.options.handlers?.onFrame?.(buffer),
        () => undefined,
      );
    }
  }

  private handleControl(raw: string): void {
    const message = parseServerMessage(raw);
    if (!message) return; // Unknown `t` is ignored by design — PROTOCOL.md §4.

    const handlers = this.options.handlers;
    const fields = message as unknown as Record<string, unknown>;

    switch (message.t) {
      case 'ready': {
        // `session` is what distinguishes ListenerReady from SenderReady (§4.3 vs
        // §4.4). A present-but-malformed one is not a ready we can act on.
        const session = asSessionPublic(fields['session']);
        if (!session) return;
        const format = asFormat(fields['format']);
        this.attempt = 0;
        this.sawReady = true;
        handlers?.onSession?.(
          session,
          typeof fields['sessionId'] === 'string' ? fields['sessionId'] : '',
        );
        handlers?.onFormat?.(format);
        this.setState(session.live && format ? 'live' : 'waiting', {});
        return;
      }
      case 'stream-start':
        handlers?.onFormat?.(asFormat(fields['format']));
        this.setState('live', {});
        return;
      case 'stream-end': {
        const reason = typeof fields['reason'] === 'string' ? fields['reason'] : 'stream ended';
        handlers?.onStreamEnd?.(reason);
        this.setState('waiting', { reason });
        return;
      }
      case 'listeners': {
        const count = asCount(fields['count']);
        if (count !== null) handlers?.onListeners?.(count);
        return;
      }
      case 'meta':
        if (typeof fields['name'] === 'string' && fields['name'] !== '') {
          handlers?.onName?.(fields['name']);
        }
        return;
      case 'pong': {
        const ts = fields['ts'];
        if (typeof ts !== 'number' || !Number.isFinite(ts)) return;
        const rtt = this.options.now() - ts;
        if (Number.isFinite(rtt) && rtt >= 0) handlers?.onRoundTrip?.(rtt);
        return;
      }
      case 'error': {
        // Both strings end up in the UI, so neither may arrive as `undefined`.
        const error: ErrorMessage = {
          t: 'error',
          code: (typeof fields['code'] === 'string' ? fields['code'] : 'internal') as ErrorCode,
          message: typeof fields['message'] === 'string' ? fields['message'] : '',
          fatal: fields['fatal'] === true,
        };
        handlers?.onProtocolError?.(error);
        if (error.fatal) {
          this.fatal = error;
          this.setState('failed', { reason: error.message || error.code });
        }
        return;
      }
      default:
        return;
    }
  }

  private handleClose(code: number, reason: string): void {
    if (this.stopped) {
      this.setState('idle', {});
      return;
    }
    if (this.fatal || !isRetryableClose(code)) {
      this.setState('failed', { reason: this.fatal?.message || reason || `close code ${code}` });
      return;
    }
    this.scheduleRetry(reason || `close code ${code}`);
  }

  private scheduleRetry(reason: string): void {
    if (this.stopped) return;
    // A connection that got as far as `ready` is healthy; treat the next failure as
    // a fresh outage rather than continuing an old backoff ramp.
    if (this.sawReady) this.attempt = 0;

    const delay = backoffDelay(
      this.attempt,
      this.options.backoff ?? DEFAULT_BACKOFF,
      this.options.random,
    );
    this.attempt += 1;
    this.setState('reconnecting', { attempt: this.attempt, retryInMs: delay, reason });
    this.retryHandle = this.options.setTimer(() => {
      this.retryHandle = null;
      if (!this.stopped) this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatHandle = this.options.setTimer(() => {
      this.heartbeatHandle = null;
      this.onHeartbeat();
    }, LIMITS.HEARTBEAT_INTERVAL_MS);
  }

  private onHeartbeat(): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== OPEN) return;

    if (this.options.now() - this.lastMessageAt > LIMITS.HEARTBEAT_TIMEOUT_MS) {
      // Silent peer. Drop it ourselves so the retry ladder starts now rather than
      // waiting for the OS to notice a half-open TCP connection.
      this.teardownSocket(1001, 'heartbeat timeout');
      this.scheduleRetry('the relay stopped responding');
      return;
    }
    this.send({ t: 'ping', ts: this.options.now() });
    this.startHeartbeat();
  }

  private stopHeartbeat(): void {
    if (this.heartbeatHandle !== null) {
      this.options.clearTimer(this.heartbeatHandle);
      this.heartbeatHandle = null;
    }
  }

  private clearRetry(): void {
    if (this.retryHandle !== null) {
      this.options.clearTimer(this.retryHandle);
      this.retryHandle = null;
    }
  }

  private send(message: unknown): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== OPEN) return;
    try {
      socket.send(JSON.stringify(message));
    } catch {
      /* The close handler deals with a dead socket. */
    }
  }

  private teardownSocket(code: number, reason: string): void {
    const socket = this.socket;
    if (!socket) return;
    this.socket = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    try {
      socket.close(code, reason);
    } catch {
      /* Already closing. */
    }
  }

  private setState(state: ConnectionState, detail: StateDetail): void {
    if (this.state === state && detail.attempt === undefined && detail.retryInMs === undefined) {
      return;
    }
    this.state = state;
    this.options.handlers?.onState?.(state, detail);
  }
}
